import { useContext } from "react";
import { Table } from "react-bootstrap";
import type { TradeGenerationData } from "../lib/explainTypes";
import { RebalanceContext } from "../../RebalanceInfo";
import { formatCurrency } from "../../../lib/numbers";

interface Props {
  trades: TradeGenerationData | null;
  isTaxable: boolean;
  accountId: number;
}

const num = (s: string | undefined): number =>
  s == null || s === "" ? 0 : Number(s);

const sideFactor = (side: string): number => (side === "buy" ? 1 : -1);

const formatShares = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: 4 });

// Reads `account.trade_generation` plus `RebalanceContext.household` for
// the current-holdings join. Mirrors the columns on the legacy
// `/rebalances/:id/trades` editor (read-only): each trade can be
// evaluated in isolation against the position it touches.
//
// `reason` is omitted -- every emitted trade carries the same hardcoded
// "Rebalancing to target weight" string today (`explain_v2.py:1515`),
// so the column would be a constant.
export default function TradesSection({ trades, isTaxable, accountId }: Props) {
  const { household } = useContext(RebalanceContext);
  const account = household?.accounts?.find((a) => a.id === accountId);
  const holdings = account?.holdings ?? [];

  // A position can be multi-lot; aggregate units and unrealized G/L per symbol.
  const positionBySymbol = new Map<
    string,
    { units: number; unrealized: number }
  >();
  for (const h of holdings) {
    const sym = h.security?.symbol;
    if (!sym) continue;
    const prev = positionBySymbol.get(sym) ?? { units: 0, unrealized: 0 };
    positionBySymbol.set(sym, {
      units: prev.units + h.units,
      unrealized: prev.unrealized + (h.unrealizedGainLoss ?? 0),
    });
  }

  if (!trades || trades.trades.length === 0) {
    return (
      <div className="text-muted">No trades required — already at target.</div>
    );
  }

  let totalTradeValue = 0;
  let totalRealized = 0;

  const rows = trades.trades.map((t, i) => {
    const tradeShares = num(t.shares);
    const tradeValue = num(t.value);
    const price = tradeShares > 0 ? tradeValue / tradeShares : 0;
    const pos = positionBySymbol.get(t.symbol);
    const currentShares = pos?.units ?? 0;
    const factor = sideFactor(t.side);
    const postShares = currentShares + factor * tradeShares;
    const signedValue = factor * tradeValue;
    const postValue = postShares * price;
    const realized = num(t.realized_gain_st) + num(t.realized_gain_lt);
    const unrealized = pos?.unrealized ?? 0;

    totalTradeValue += signedValue;
    totalRealized += realized;

    return (
      <tr key={`${t.symbol}-${i}`}>
        <td>{t.symbol}</td>
        <td className="text-end">{formatShares(currentShares)}</td>
        <td>{t.side}</td>
        <td className="text-end">{formatCurrency(price)}</td>
        <td className="text-end">{formatShares(tradeShares)}</td>
        <td className="text-end">{formatShares(postShares)}</td>
        <td className="text-end">{formatCurrency(signedValue)}</td>
        <td className="text-end">{formatCurrency(postValue)}</td>
        {isTaxable && <td className="text-end">{formatCurrency(realized)}</td>}
        {isTaxable && (
          <td className="text-end">{formatCurrency(unrealized)}</td>
        )}
      </tr>
    );
  });

  return (
    <Table size="sm" responsive>
      <thead>
        <tr>
          <th>Symbol</th>
          <th className="text-end">Current Shares</th>
          <th>Side</th>
          <th className="text-end">Price</th>
          <th className="text-end">Shares</th>
          <th className="text-end">Post-Trade Shares</th>
          <th className="text-end">Trade Value</th>
          <th className="text-end">Post-Trade Value</th>
          {isTaxable && <th className="text-end">Realized G/L</th>}
          {isTaxable && <th className="text-end">Unrealized G/L</th>}
        </tr>
      </thead>
      <tbody>
        {rows}
        <tr className="fw-bold border-top">
          <td className="border-bottom-0">Total</td>
          <td className="border-bottom-0" colSpan={5}></td>
          <td className="text-end border-bottom-0">
            {formatCurrency(totalTradeValue)}
          </td>
          <td className="border-bottom-0"></td>
          {isTaxable && (
            <td className="text-end border-bottom-0">
              {formatCurrency(totalRealized)}
            </td>
          )}
          {isTaxable && <td className="border-bottom-0"></td>}
        </tr>
      </tbody>
    </Table>
  );
}
